import { BadgeUpdate, EventEditionDetails, IPersonBadge } from "../../../../../common/types";
import PublicApiService from "../../../../../services/PublicApiService";
import { useDispatch, useSelector, useStore } from "react-redux";
import { badgesSelector, eventEditionSelector } from "../../../../../redux/eventEdition/selectors/eventEdition.selectors";
import BadgeHelper from "../../../../../common/badges/helper/BadgeHelper";
import NetworkHelper from "../../../../../common/network/helpers/network/helper/NetworkHelper";
import searchFormUtils from "../utils/searchFormUtils";
import { RootState } from "../../../../../redux/store";
import tasks from "../../../../../common/tasks/tasks";
import { CustomerTypes } from "../../../../createBadge/enums/CustomerTypes";
import { useUserAccess } from "../../../../createBadge/hooks/useUserAccess";
import { UpgradeBadgeOptions } from "../types";
import cloneDeep from "lodash.clonedeep";
import { setEventEditionBadges } from "../../../../../redux/eventEdition/slice/eventEditionSlice";
import {
  setLastBadgeUpdateTime,
  setOffline,
  badgeSyncRequestStarted,
  badgeSyncRequestCompleted,
  badgeSyncRequestFailed
} from "../../../../../redux/metadata/slice/metadataSlice";
import dayjs from "dayjs";
import { UpgradeErrorType, UpgradeResponseCode } from "../../../../../common/enums";
import { NOT_ENOUGH_ENTITLEMENTS_ERROR } from "../../../../createBadge/constants";

type Props = {
  fetchDelegateUpgradeProducts?: (badge: IPersonBadge) => Promise<void>;
};

export const useBadgeUpdate = ({ fetchDelegateUpgradeProducts }: Props = {}) => {
  const dispatch = useDispatch();
  const { getState } = useStore();
  const eventEdition: EventEditionDetails = useSelector(eventEditionSelector).eventEdition!;
  const badges = useSelector(badgesSelector);
  const { options } = useUserAccess();

  const handleApiUpdateCalls = async (badgeDto: BadgeUpdate, upgradeBadgeOptions?: UpgradeBadgeOptions, correlationId?: string) => {
    try {
      if (upgradeBadgeOptions?.magentoPaymentMethod && upgradeBadgeOptions?.cartId) {
        badgeDto.paymentMethod = upgradeBadgeOptions.magentoPaymentMethod;
        badgeDto.cartId = upgradeBadgeOptions.cartId;
        const updateResponse = await PublicApiService.updateBadge(badgeDto, correlationId);
        if (updateResponse.data.responseCode !== UpgradeResponseCode.SUCCESS) {
          throw Error();
        }
        return updateResponse.data;
      } else {
        if (badgeDto.companyProductCode) {
          delete badgeDto.companyProductCode;
        }
        await PublicApiService.updateBadgesBatch(eventEdition.gbsCode, [badgeDto], correlationId);
      }
    } catch (e: any) {
      console.error(e);
      if (e?.response?.data?.error?.code?.includes(NOT_ENOUGH_ENTITLEMENTS_ERROR)) {
        throw Error(UpgradeErrorType.ENTITLEMENT);
      }
      throw Error(UpgradeErrorType.UPGRADE);
    }
  };

  const checkConnection = async () => {
    const isConnected = await NetworkHelper.isConnected();
    if (!isConnected) {
      throw Error(UpgradeErrorType.NETWORK);
    }
  };

  const handleBadgeUpdate = async (
    badge: IPersonBadge,
    orderId?: string,
    upgradeBadgeOptions?: UpgradeBadgeOptions,
    correlationId?: string
  ) => {
    const updatedBadges = searchFormUtils.getBadgesWithReplacedItem(badges, badge);
    dispatch(setEventEditionBadges(updatedBadges));
    await checkConnection();

    const badgeDto = BadgeHelper.getUpdateBadgeDto(badge, eventEdition, options, orderId);
    if (badge.customerType === CustomerTypes.CONTRACTOR) {
      badge.supportStaffDetails.userAccess = null;
      badgeDto.supportStaffDetails.userAccess = null;
    }

    if (upgradeBadgeOptions?.companyProductCode) {
      badgeDto.companyProductCode = upgradeBadgeOptions.companyProductCode;
    }

    if (upgradeBadgeOptions?.paymentReference) {
      badgeDto.payments = [
        {
          paymentMethod: upgradeBadgeOptions.magentoPaymentMethod || "",
          paymentReference: upgradeBadgeOptions.paymentReference,
          amount: upgradeBadgeOptions.priceWithTax!
        }
      ];
      badgeDto.paymentDate = dayjs().format();
    }

    const badgeUpdateResponse = await handleApiUpdateCalls(badgeDto, upgradeBadgeOptions, correlationId);

    if (fetchDelegateUpgradeProducts && badge.customerType === CustomerTypes.EXHIBITING_DELEGATE) {
      await fetchDelegateUpgradeProducts(badge);
    }
    return badgeUpdateResponse;
  };

  const getUpdatedBadges = async (minutes: number) => {
    try {
      dispatch(badgeSyncRequestStarted());
      const store = getState() as RootState;
      const lastUpdatedDate = store.metadata.lastBadgeUpdateTime;
      const eventEdition = store.eventEdition.eventEdition;
      const badgesFromStore = [...store.eventEdition.badges] as IPersonBadge[];
      const modifiedDate = tasks.substractXMinsFromDate(lastUpdatedDate, minutes);

      const fetchBadgesSince = async (gbsCode: string) => {
        const badgesResponse = await PublicApiService.getOdinBadges(gbsCode, "", modifiedDate);
        let token = badgesResponse.token;
        while (token) {
          const { results, token: newToken } = await PublicApiService.getOdinBadges(gbsCode, token, modifiedDate);
          badgesResponse.results = badgesResponse.results.concat(results);
          token = newToken;
        }
        return badgesResponse.results;
      };

      let latestBadges: IPersonBadge[] = [];
      latestBadges = await fetchBadgesSince(eventEdition!.gbsCode);
      let updatedBadges = cloneDeep(badgesFromStore) as IPersonBadge[];

      if (store.metadata.offline) {
        dispatch(setOffline(false));
      }

      const arrayOfBadgeIds = updatedBadges.map(badge => badge.id);

      for (let i = 0; i < latestBadges.length; i++) {
        const index = arrayOfBadgeIds.indexOf(latestBadges[i]!.id);
        if (index === -1) {
          updatedBadges.push(latestBadges[i]!);
        } else {
          updatedBadges[index] = latestBadges[i]!;
        }
      }

      if (updatedBadges.length !== 0) {
        dispatch(setEventEditionBadges(updatedBadges));
      }
      dispatch(setLastBadgeUpdateTime(dayjs(new Date()).format()));
      dispatch(badgeSyncRequestCompleted());
    } catch (e) {
      console.error(e);

      dispatch(badgeSyncRequestFailed());
    }
  };

  return { handleBadgeUpdate, getUpdatedBadges };
};
